// NotFound404.tsx
// Halaman 404 Not Found — React TypeScript
// Tidak membutuhkan library tambahan

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  type FC,
} from "react";

// ─────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
}

interface GlitchState {
  active: boolean;
  offset: number;
}

// ─────────────────────────────────────────────────────────
// GLOBAL CSS
// ─────────────────────────────────────────────────────────

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

:root {
  --bg: #0d1117;
  --surface: #161b22;
  --surface2: #1c2330;
  --border: #21262d;
  --accent: #00d4aa;
  --accent2: #0095ff;
  --accent3: #ff6b6b;
  --accent4: #ffd166;
  --text: #e6edf3;
  --text-muted: #7d8590;
  --text-dim: #484f58;
}

html, body, #root {
  height: 100%;
  font-family: 'DM Sans', sans-serif;
  background: var(--bg);
  color: var(--text);
  overflow: hidden;
}

::-webkit-scrollbar { display: none; }

/* ── PAGE ── */
.page-404 {
  position: relative;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* ── CANVAS BACKGROUND ── */
.canvas-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

/* ── GRID LINES ── */
.grid-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image:
    linear-gradient(rgba(0,212,170,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,212,170,0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  animation: gridShift 20s linear infinite;
}
@keyframes gridShift {
  from { background-position: 0 0; }
  to   { background-position: 60px 60px; }
}

/* ── GLOW ORBS ── */
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
  animation: orbFloat 8s ease-in-out infinite;
}
.orb-1 {
  width: 400px; height: 400px;
  background: rgba(0,212,170,0.06);
  top: -100px; left: -100px;
  animation-delay: 0s;
}
.orb-2 {
  width: 350px; height: 350px;
  background: rgba(0,149,255,0.05);
  bottom: -80px; right: -80px;
  animation-delay: -3s;
}
.orb-3 {
  width: 250px; height: 250px;
  background: rgba(255,107,107,0.04);
  top: 50%; left: 50%;
  transform: translate(-50%,-50%);
  animation-delay: -6s;
}
@keyframes orbFloat {
  0%,100% { transform: scale(1) translate(0,0); }
  33%      { transform: scale(1.1) translate(20px,-20px); }
  66%      { transform: scale(0.9) translate(-20px,20px); }
}

/* ── CONTENT ── */
.content-404 {
  position: relative;
  z-index: 10;
  text-align: center;
  padding: 40px 24px;
  max-width: 580px;
  width: 100%;
  animation: contentIn 0.8s cubic-bezier(0.22,1,0.36,1) both;
}
@keyframes contentIn {
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── 404 NUMBER ── */
.num-404-wrap {
  position: relative;
  display: inline-block;
  margin-bottom: 8px;
}
.num-404 {
  font-family: 'DM Serif Display', serif;
  font-size: clamp(100px, 18vw, 180px);
  font-weight: 400;
  line-height: 1;
  letter-spacing: -4px;
  color: transparent;
  -webkit-text-stroke: 1px rgba(0,212,170,0.3);
  position: relative;
  z-index: 1;
  user-select: none;
}
.num-404-shadow {
  position: absolute;
  inset: 0;
  font-family: 'DM Serif Display', serif;
  font-size: clamp(100px, 18vw, 180px);
  font-weight: 400;
  line-height: 1;
  letter-spacing: -4px;
  color: transparent;
  -webkit-text-stroke: 1px rgba(0,212,170,0.08);
  transform: translate(3px, 3px);
  z-index: 0;
  user-select: none;
}
.num-404-glow {
  position: absolute;
  inset: 0;
  font-family: 'DM Serif Display', serif;
  font-size: clamp(100px, 18vw, 180px);
  font-weight: 400;
  line-height: 1;
  letter-spacing: -4px;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent2) 50%, var(--accent) 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  opacity: 0.15;
  animation: shimmer 3s linear infinite;
  z-index: 0;
  user-select: none;
}
@keyframes shimmer {
  from { background-position: 0% center; }
  to   { background-position: 200% center; }
}

/* GLITCH LAYERS */
.num-404-glitch-r,
.num-404-glitch-b {
  position: absolute;
  inset: 0;
  font-family: 'DM Serif Display', serif;
  font-size: clamp(100px, 18vw, 180px);
  font-weight: 400;
  line-height: 1;
  letter-spacing: -4px;
  user-select: none;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.05s;
}
.num-404-glitch-r { color: rgba(255,107,107,0.5); }
.num-404-glitch-b { color: rgba(0,149,255,0.5); }
.num-404-glitch-r.on { opacity: 1; }
.num-404-glitch-b.on { opacity: 1; }

/* ── STATUS BADGE ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 40px;
  padding: 6px 16px;
  font-size: 12px;
  font-family: 'DM Mono', monospace;
  color: var(--text-muted);
  margin-bottom: 24px;
  animation: badgeIn 0.6s ease 0.2s both;
}
@keyframes badgeIn {
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1); }
}
.status-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--accent3);
  animation: blink 2s ease infinite;
}
@keyframes blink {
  0%,100% { opacity: 1; }
  50%      { opacity: 0.2; }
}
.status-code {
  color: var(--accent3);
  font-weight: 500;
}

/* ── TITLE & DESC ── */
.title-404 {
  font-family: 'DM Serif Display', serif;
  font-size: clamp(22px, 4vw, 32px);
  color: var(--text);
  margin-bottom: 12px;
  animation: contentIn 0.8s ease 0.15s both;
}
.desc-404 {
  font-size: 15px;
  color: var(--text-muted);
  line-height: 1.7;
  margin-bottom: 36px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
  animation: contentIn 0.8s ease 0.25s both;
}
.desc-404 code {
  font-family: 'DM Mono', monospace;
  font-size: 13px;
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--accent);
  padding: 1px 7px;
  border-radius: 5px;
}

/* ── TERMINAL BLOCK ── */
.terminal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 32px;
  text-align: left;
  animation: contentIn 0.8s ease 0.3s both;
}
.terminal-header {
  background: var(--surface2);
  border-bottom: 1px solid var(--border);
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.t-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
}
.t-red    { background: #ff5f56; }
.t-yellow { background: #ffbd2e; }
.t-green  { background: #27c93f; }
.terminal-title {
  margin-left: auto;
  font-size: 11px;
  color: var(--text-dim);
  font-family: 'DM Mono', monospace;
}
.terminal-body {
  padding: 16px;
  font-family: 'DM Mono', monospace;
  font-size: 13px;
  line-height: 2;
}
.t-line { display: flex; align-items: flex-start; gap: 8px; }
.t-prompt { color: var(--accent); flex-shrink: 0; }
.t-cmd    { color: var(--text-muted); }
.t-out    { color: var(--text-dim); padding-left: 16px; }
.t-error  { color: var(--accent3); padding-left: 16px; }
.t-cursor {
  display: inline-block;
  width: 8px; height: 14px;
  background: var(--accent);
  margin-left: 2px;
  vertical-align: middle;
  animation: cursorBlink 1s step-end infinite;
}
@keyframes cursorBlink {
  0%,100% { opacity: 1; }
  50%      { opacity: 0; }
}

/* ── BUTTONS ── */
.btn-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  animation: contentIn 0.8s ease 0.4s both;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22,1,0.36,1);
  border: 1px solid var(--border);
  text-decoration: none;
  outline: none;
}
.btn:active { transform: scale(0.97); }
.btn-primary {
  background: var(--accent);
  border-color: var(--accent);
  color: #0d1117;
  font-weight: 600;
}
.btn-primary:hover {
  background: #00f0c0;
  box-shadow: 0 0 20px rgba(0,212,170,0.25);
}
.btn-secondary {
  background: var(--surface);
  color: var(--text-muted);
}
.btn-secondary:hover {
  border-color: rgba(255,255,255,0.2);
  color: var(--text);
  background: var(--surface2);
}
.btn-ghost {
  background: transparent;
  color: var(--text-muted);
  border-color: transparent;
}
.btn-ghost:hover { color: var(--accent); }

/* ── SUGGESTIONS ── */
.suggestions {
  margin-top: 40px;
  animation: contentIn 0.8s ease 0.5s both;
}
.suggest-label {
  font-size: 11px;
  color: var(--text-dim);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 12px;
}
.suggest-grid {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}
.suggest-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 12px;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s;
}
.suggest-pill:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: rgba(0,212,170,0.05);
}
.suggest-icon { font-size: 13px; }

/* ── BOTTOM SIGNATURE ── */
.bottom-sig {
  position: absolute;
  bottom: 24px;
  left: 0; right: 0;
  text-align: center;
  font-size: 11px;
  color: var(--text-dim);
  font-family: 'DM Mono', monospace;
  z-index: 10;
  animation: contentIn 1s ease 0.7s both;
}
.bottom-sig span { color: var(--text-dim); }
.bottom-sig .accent { color: var(--accent); }

/* ── FLOATING PARTICLES ── */
.particle {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 1;
}

/* ── SCAN LINE ── */
.scanline {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0,212,170,0.15), transparent);
  z-index: 5;
  pointer-events: none;
  animation: scanDown 6s linear infinite;
}
@keyframes scanDown {
  from { top: -2px; }
  to   { top: 100vh; }
}

/* ── CORNER DECORATIONS ── */
.corner {
  position: absolute;
  width: 40px; height: 40px;
  border-color: rgba(0,212,170,0.2);
  border-style: solid;
  z-index: 5;
  pointer-events: none;
}
.corner-tl { top: 24px; left: 24px; border-width: 1px 0 0 1px; }
.corner-tr { top: 24px; right: 24px; border-width: 1px 1px 0 0; }
.corner-bl { bottom: 24px; left: 24px; border-width: 0 0 1px 1px; }
.corner-br { bottom: 24px; right: 24px; border-width: 0 1px 1px 0; }
`;

// ─────────────────────────────────────────────────────────
// INJECT CSS
// ─────────────────────────────────────────────────────────

const GlobalStyles: FC = () => {
  useEffect(() => {
    const id = "styles-404";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
  return null;
};

// ─────────────────────────────────────────────────────────
// PARTICLE SYSTEM
// ─────────────────────────────────────────────────────────

const PARTICLE_COLORS = [
  "rgba(0,212,170,0.6)",
  "rgba(0,149,255,0.5)",
  "rgba(255,209,102,0.4)",
  "rgba(255,107,107,0.4)",
];

function createParticle(id: number): Particle {
  return {
    id,
    x: Math.random() * window.innerWidth,
    y: Math.random() * window.innerHeight,
    vx: (Math.random() - 0.5) * 0.4,
    vy: (Math.random() - 0.5) * 0.4,
    size: Math.random() * 2.5 + 0.5,
    opacity: Math.random() * 0.5 + 0.1,
    color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
  };
}

const ParticleSystem: FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Init particles
    particlesRef.current = Array.from({ length: 60 }, (_, i) => createParticle(i));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach(p => {
        // Move
        p.x += p.vx;
        p.y += p.vy;
        // Wrap
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
        // Draw
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace(/[\d.]+\)$/, `${p.opacity})`);
        ctx.fill();
      });
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="canvas-bg"
      style={{ pointerEvents: "none" }}
    />
  );
};

// ─────────────────────────────────────────────────────────
// GLITCH EFFECT HOOK
// ─────────────────────────────────────────────────────────

function useGlitch(intervalMs = 4000) {
  const [glitch, setGlitch] = useState<GlitchState>({ active: false, offset: 0 });

  useEffect(() => {
    const trigger = () => {
      const offset = Math.random() * 6 - 3;
      setGlitch({ active: true, offset });
      const dur = Math.random() * 120 + 60;
      setTimeout(() => setGlitch({ active: false, offset: 0 }), dur);
    };

    // Random glitch intervals
    let timeout: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const delay = intervalMs + Math.random() * 2000 - 1000;
      timeout = setTimeout(() => {
        trigger();
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timeout);
  }, [intervalMs]);

  return glitch;
}

// ─────────────────────────────────────────────────────────
// TYPING EFFECT HOOK
// ─────────────────────────────────────────────────────────

function useTyping(text: string, delay: number = 0, speed: number = 40) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDisplayed("");
    setDone(false);
    let i = 0;
    const startTimeout = setTimeout(() => {
      const interval = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(interval);
          setDone(true);
        }
      }, speed);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(startTimeout);
  }, [text, delay, speed]);

  return { displayed, done };
}

// ─────────────────────────────────────────────────────────
// TERMINAL BLOCK
// ─────────────────────────────────────────────────────────

const TerminalBlock: FC = () => {
  const cmd1 = useTyping("GET /dashboard/unknown-page HTTP/1.1", 600, 30);
  const cmd2 = useTyping("curl -I https://app.hrco.id/xyz", cmd1.done ? 200 : 99999, 25);

  return (
    <div className="terminal">
      <div className="terminal-header">
        <div className="t-dot t-red" />
        <div className="t-dot t-yellow" />
        <div className="t-dot t-green" />
        <span className="terminal-title">console — hr-system</span>
      </div>
      <div className="terminal-body">
        {/* Line 1: request */}
        <div className="t-line">
          <span className="t-prompt">$</span>
          <span className="t-cmd">{cmd1.displayed}{!cmd1.done && <span className="t-cursor" />}</span>
        </div>
        {cmd1.done && (
          <>
            <div className="t-line">
              <span className="t-error">✗  404 Not Found — The resource you requested does not exist.</span>
            </div>
            <div className="t-line">
              <span className="t-out">Trace: router.resolve() → RouteNotFoundError</span>
            </div>
          </>
        )}

        {/* Line 2: curl */}
        {cmd1.done && (
          <div className="t-line" style={{ marginTop: 8 }}>
            <span className="t-prompt">$</span>
            <span className="t-cmd">{cmd2.displayed}{!cmd2.done && <span className="t-cursor" />}</span>
          </div>
        )}
        {cmd2.done && (
          <>
            <div className="t-line">
              <span className="t-out">HTTP/2 404</span>
            </div>
            <div className="t-line">
              <span className="t-out">content-type: text/html; charset=utf-8</span>
            </div>
            <div className="t-line">
              <span className="t-out">x-error-code: PAGE_NOT_FOUND</span>
            </div>
            <div className="t-line">
              <span className="t-prompt">$</span>
              <span className="t-cursor" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// SUGGESTIONS
// ─────────────────────────────────────────────────────────

const SUGGESTIONS = [
  { icon: "⬛", label: "Dashboard",     path: "/index.html"    },
  { icon: "👤", label: "Karyawan",      path: "/karyawan.html" },
  { icon: "🕐", label: "Kehadiran",     path: "/kehadiran.html"},
  { icon: "⏱️", label: "Lembur",        path: "/lembur.html"   },
  { icon: "📊", label: "Status",        path: "/status-kehadiran.html" },
];

// ─────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────

const NotFound404: FC = () => {
  const glitch = useGlitch(4000);

  const handleBack = useCallback(() => {
    window.history.back();
  }, []);

  const handleHome = useCallback(() => {
    // Ganti dengan router navigation jika pakai React Router:
    // navigate("/")
    window.location.href = "/";
  }, []);

  const handleSuggest = useCallback((path: string) => {
    window.location.href = path;
  }, []);

  return (
    <>
      <GlobalStyles />
      <div className="page-404">

        {/* BACKGROUND LAYERS */}
        <div className="grid-bg" />
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <ParticleSystem />
        <div className="scanline" />

        {/* CORNER DECORATIONS */}
        <div className="corner corner-tl" />
        <div className="corner corner-tr" />
        <div className="corner corner-bl" />
        <div className="corner corner-br" />

        {/* MAIN CONTENT */}
        <div className="content-404">

          {/* STATUS BADGE */}
          <div className="status-badge">
            <div className="status-dot" />
            <span>HTTP</span>
            <span className="status-code">404</span>
            <span>·</span>
            <span>PAGE_NOT_FOUND</span>
          </div>

          {/* 404 NUMBER with glitch */}
          <div className="num-404-wrap">
            <div
              className="num-404-glitch-r"
              style={{
                transform: glitch.active ? `translate(${glitch.offset * 2}px, -2px)` : "none",
              }}
              aria-hidden
            >
              404
            </div>
            <div
              className="num-404-glitch-b"
              style={{
                transform: glitch.active ? `translate(${-glitch.offset}px, 2px)` : "none",
              }}
              aria-hidden
            >
              404
            </div>
            <div className="num-404-glow" aria-hidden>404</div>
            <div
              className="num-404"
              style={{
                transform: glitch.active
                  ? `translate(${glitch.offset * 0.5}px, 0)`
                  : "none",
                transition: glitch.active ? "none" : "transform 0.1s ease",
              }}
            >
              404
            </div>
            <div className="num-404-shadow" aria-hidden>404</div>
          </div>

          {/* TITLE */}
          <h1 className="title-404">Halaman tidak ditemukan</h1>

          {/* DESC */}
          <p className="desc-404">
            URL <code>{typeof window !== "undefined" ? window.location.pathname : "/unknown"}</code> tidak ada
            di sistem. Mungkin sudah dipindah, dihapus, atau kamu salah ketik.
          </p>

          {/* TERMINAL */}
          <TerminalBlock />

          {/* BUTTONS */}
          <div className="btn-group">
            <button className="btn btn-primary" onClick={handleHome}>
              ⬛ Ke Dashboard
            </button>
            <button className="btn btn-secondary" onClick={handleBack}>
              ← Kembali
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => window.location.reload()}
            >
              ↻ Refresh
            </button>
          </div>

          {/* SUGGESTIONS */}
          <div className="suggestions">
            <p className="suggest-label">Mungkin yang kamu cari</p>
            <div className="suggest-grid">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s.label}
                  className="suggest-pill"
                  onClick={() => handleSuggest(s.path)}
                >
                  <span className="suggest-icon">{s.icon}</span>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM SIGNATURE */}
        <div className="bottom-sig">
          <span>HR·SYSTEM</span>
          <span> · </span>
          <span className="accent">error 404</span>
          <span> · </span>
          <span>{new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
      </div>
    </>
  );
};

export default NotFound404;